import { useState, useEffect } from 'react';
import { Input, Select } from '@react-apps/design-system';
import { useProfile, useUpdateProfile } from '../hooks/use-profile';
import { useSendVerification } from '@/modules/auth/presentation/hooks/use-send-verification';
import type { Address } from '../../domain/entities/profile.entity';
import {
  detectDocumentType,
  formatDocumentForDisplay,
} from '@/shared/utils/br-fields';
import { DocumentValidityBadge } from '@/shared/components/document-validity-badge';

export function ProfilePage() {
  const { data: profile, isLoading } = useProfile();
  // Verificação vem da conta no nosso banco (`/users/me`), nunca do
  // `emailVerified` da sessão Firebase: quem define a senha pelo link de
  // primeiro acesso já provou posse do e-mail, e é o banco que registra isso.
  const isEmailVerified = profile?.isVerified ?? false;
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const {
    send: sendVerification,
    isLoading: isSending,
    success: verificationSent,
    error: verificationError,
  } = useSendVerification();

  const updateProfileMutation = useUpdateProfile();
  const docTypeLabel =
    detectDocumentType(profile?.doc) === 'cnpj' ? 'CNPJ' : 'CPF';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [zipCode, setZipCode] = useState('');

  useEffect(() => {
    if (profile) {
      const parts = (profile.name ?? '').split(' ');
      setFirstName(parts[0] ?? '');
      setLastName(parts.slice(1).join(' '));
      setPhone(profile.phone ?? '');
      setGender(profile.gender ?? '');
      setStreet(profile.address?.street ?? '');
      setCity(profile.address?.city ?? '');
      setState(profile.address?.state ?? '');
      setCountry(profile.address?.country ?? '');
      setZipCode(profile.address?.zipCode ?? '');
    }
  }, [profile]);

  const profileParts = (profile?.name ?? '').split(' ');
  const isDirty =
    firstName !== (profileParts[0] ?? '') ||
    lastName !== profileParts.slice(1).join(' ') ||
    phone !== (profile?.phone ?? '') ||
    gender !== (profile?.gender ?? '') ||
    street !== (profile?.address?.street ?? '') ||
    city !== (profile?.address?.city ?? '') ||
    state !== (profile?.address?.state ?? '') ||
    country !== (profile?.address?.country ?? '') ||
    zipCode !== (profile?.address?.zipCode ?? '');

  const resetForm = () => {
    const parts = (profile?.name ?? '').split(' ');
    setFirstName(parts[0] ?? '');
    setLastName(parts.slice(1).join(' '));
    setPhone(profile?.phone ?? '');
    setGender(profile?.gender ?? '');
    setStreet(profile?.address?.street ?? '');
    setCity(profile?.address?.city ?? '');
    setState(profile?.address?.state ?? '');
    setCountry(profile?.address?.country ?? '');
    setZipCode(profile?.address?.zipCode ?? '');
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits.replace(/^(\d{0,2})/, '($1');
    if (digits.length <= 6)
      return digits.replace(/^(\d{2})(\d{0,4})/, '($1) $2');
    if (digits.length <= 10)
      return digits.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    return digits.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  };

  const handleSubmitProfile = (e: { preventDefault(): void }) => {
    e.preventDefault();
    const name = [firstName, lastName].filter(Boolean).join(' ');
    const address: Address = { street, city, state, country, zipCode };
    updateProfileMutation.mutate({
      name,
      phone,
      gender: gender || undefined,
      address,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-(--color-accent) h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-240 mx-auto space-y-6">
      <div>
        <h1 className="text-(--color-text-primary) text-2xl font-bold">
          Dados Pessoais
        </h1>
        <p className="text-(--color-text-secondary) mt-1 text-sm">
          Atualize suas informações cadastrais
        </p>
      </div>

      <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-6">
        <form onSubmit={handleSubmitProfile} className="space-y-5">
          {/* Nome + Sobrenome */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="firstName"
              label="Nome"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Maria"
            />
            <Input
              id="lastName"
              label="Sobrenome"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Silva"
            />
          </div>

          {/* Documento + E-mail */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <label
                  htmlFor="doc"
                  className="text-(--color-text-secondary) text-sm font-medium"
                >
                  {docTypeLabel}
                </label>
                <DocumentValidityBadge value={profile?.doc} />
              </div>
              <Input
                id="doc"
                type="text"
                disabled
                value={formatDocumentForDisplay(profile?.doc) ?? ''}
                placeholder="123.456.789-00"
                helperText={`O ${docTypeLabel} não pode ser alterado`}
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="text-(--color-text-secondary) mb-1.5 block text-sm font-medium"
              >
                E-mail
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  disabled
                  value={profile?.email ?? ''}
                  className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) w-full cursor-not-allowed rounded-lg border px-3 py-2.5 pr-28 text-sm outline-none"
                />
                {isEmailVerified ? (
                  <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-400">
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                    Verificado
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowVerifyDialog(true)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 transition hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:hover:bg-amber-900"
                  >
                    Verificar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Telefone + Gênero */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="phone"
              label="Telefone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(11) 98765-4321"
            />
            <Select
              id="gender"
              label="Gênero"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              options={[
                { value: '', label: 'Não informado' },
                { value: 'MALE', label: 'Masculino' },
                { value: 'FEMALE', label: 'Feminino' },
                { value: 'OTHER', label: 'Outro' },
              ]}
            />
          </div>

          {/* Endereço */}
          <div className="space-y-3">
            <p className="text-(--color-text-secondary) text-sm font-medium">
              Endereço
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="sm:col-span-2">
                <Input
                  id="street"
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Rua"
                  aria-label="Rua"
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  id="city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Cidade"
                  aria-label="Cidade"
                />
              </div>
              <div>
                <Input
                  id="state"
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="Estado"
                  aria-label="Estado"
                />
              </div>
              <div>
                <Input
                  id="country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="País"
                  aria-label="País"
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  id="zipCode"
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="CEP"
                  aria-label="CEP"
                />
              </div>
            </div>
          </div>

          {updateProfileMutation.isSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400">
              Dados atualizados com sucesso.
            </p>
          )}
          {updateProfileMutation.isError && (
            <p className="text-sm text-red-500">
              {updateProfileMutation.error.message}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={!isDirty || updateProfileMutation.isPending}
              className="bg-(--color-accent) rounded-lg px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {updateProfileMutation.isPending
                ? 'Salvando…'
                : 'Salvar alterações'}
            </button>
            {isDirty && !updateProfileMutation.isPending && (
              <button
                type="button"
                onClick={resetForm}
                disabled={!isDirty || updateProfileMutation.isPending}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Verify email dialog */}
      {showVerifyDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setShowVerifyDialog(false)}
        >
          <div
            className="border-(--color-border) bg-(--color-bg-primary) relative w-full max-w-sm rounded-2xl border p-8 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowVerifyDialog(false)}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) absolute right-4 top-4 rounded-lg p-1 transition-colors"
              aria-label="Fechar"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950">
                <svg
                  className="h-8 w-8 text-blue-600 dark:text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
              </div>

              <h2 className="text-(--color-text-primary) mb-2 text-xl font-bold">
                Verifique seu e-mail
              </h2>
              <p className="text-(--color-text-secondary) mb-6 text-sm">
                Enviamos um link de verificação para o seu e-mail. Clique no
                link para ativar sua conta.
              </p>

              {verificationError && (
                <p className="mb-4 text-sm text-red-500">{verificationError}</p>
              )}

              {verificationSent ? (
                <p className="text-sm font-medium text-green-600 dark:text-green-400">
                  E-mail enviado! Verifique sua caixa de entrada.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => void sendVerification()}
                  disabled={isSending}
                  className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition disabled:opacity-50"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                  {isSending ? 'Enviando…' : 'Reenviar e-mail'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
